import React, { useState } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Shield, 
  Edit, 
  Save, 
  X, 
  Camera,
  Key,
  Bell,
  Globe,
  Briefcase,
  Award,
  Clock
} from 'lucide-react';

const ProfileModal = ({ isOpen, onClose, user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [formData, setFormData] = useState({
    firstName: user?.firstName || 'John',
    lastName: user?.lastName || 'Doe',
    email: user?.email || 'admin@assetmanagement.com',
    phone: user?.phone || '+15550002',
    department: user?.department || 'Administration',
    position: user?.position || 'System Administrator',
    location: user?.location || 'New York, NY',
    bio: user?.bio || 'Experienced administrator with expertise in asset management systems.',
    joinDate: user?.joinDate || '2024-01-15',
    lastLogin: user?.lastLogin || '2024-12-19',
    role: user?.role?.displayName || user?.role || 'Administrator',
    permissions: user?.permissions || []
  });

  const [preferences, setPreferences] = useState({
    notifications: {
      email: true,
      push: true,
      sms: false,
      maintenance: true,
      contracts: true,
      reports: false
    },
    language: 'en',
    timezone: 'UTC',
    theme: 'light'
  });

  const [security, setSecurity] = useState({
    twoFactor: false,
    sessionTimeout: 30,
    passwordLastChanged: '2024-01-15'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePreferenceChange = (category, key, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const handleSave = () => {
    // In a real app, this would make an API call to update the user profile
    //console.log('Saving profile data:', formData);
    setIsEditing(false);
    // Show success message
  };

  const handleCancel = () => {
    // Reset form data to original values
    setFormData({
      firstName: user?.firstName || 'John',
      lastName: user?.lastName || 'Doe',
      email: user?.email || 'admin@assetmanagement.com',
      phone: user?.phone || '+15550002',
      department: user?.department || 'Administration',
      position: user?.position || 'System Administrator',
      location: user?.location || 'New York, NY',
      bio: user?.bio || 'Experienced administrator with expertise in asset management systems.',
      joinDate: user?.joinDate || '2024-01-15',
      lastLogin: user?.lastLogin || '2024-12-19',
      role: user?.role?.displayName || user?.role || 'Administrator',
      permissions: user?.permissions || []
    });
    setIsEditing(false);
  };

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'preferences', label: 'Preferences', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'activity', label: 'Activity', icon: Clock }
  ];

  const formInputStyle = {
    fontFamily: 'Inter, sans-serif',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '14px',
    backgroundColor: isEditing ? '#ffffff' : '#f9fafb',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  };

  const labelStyle = {
    fontFamily: 'Inter, sans-serif', 
    fontWeight: '600',
    color: '#374151',
    fontSize: '14px',
    marginBottom: '8px'
  };

  if (!isOpen) return null;

  return (
    <div className="position-fixed d-flex align-items-center justify-content-center" 
         style={{
           top: 0,
           left: 0,
           right: 0,
           bottom: 0,
           backgroundColor: 'rgba(0, 0, 0, 0.7)',
           backdropFilter: 'blur(8px)',
           zIndex: 10000
         }}
         onClick={onClose}>
      <div 
        className="rounded-xl shadow-lg"
        style={{ 
          width: '90%', 
          maxWidth: '900px', 
          maxHeight: '90vh',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          border: '2px solid #e5e7eb',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
        onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div 
          className="d-flex justify-content-between align-items-center p-4"
          style={{
            background: 'linear-gradient(135deg, #007B8F 0%, #4A9BA8 100%)',
            color: 'white',
            borderRadius: '12px 12px 0 0'
          }}>
          <div>
            <h4 className="mb-1" style={{ fontFamily: 'Inter, sans-serif', fontWeight: '600', fontSize: '20px' }}>
              Profile Settings
            </h4>
             <p className="mb-0" style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.95)' }}>
               Manage your account information and preferences
             </p>
          </div>
          <button 
            onClick={onClose}
            className="btn btn-sm"
            style={{ 
              borderRadius: '50%', 
              width: '36px', 
              height: '36px', 
              padding: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white'
            }}>
            <X size={18} />
          </button>
        </div>

        <div className="d-flex" style={{ height: '600px' }}>
          {/* Sidebar */}
          <div 
            className="border-end" 
            style={{ 
              width: '220px', 
              backgroundColor: '#ffffff',
              borderRight: '1px solid #e5e7eb'
            }}>
            <div className="p-4">
              <div className="text-center mb-4">
                <div 
                  className="mx-auto mb-3 position-relative"
                  style={{ 
                    width: '90px', 
                    height: '90px', 
                    background: 'linear-gradient(135deg, #007B8F 0%, #4A9BA8 100%)', 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 25px rgba(0, 123, 143, 0.3)'
                  }}>
                  <User size={36} color="white" />
                  <button 
                    className="position-absolute"
                    style={{ 
                      bottom: 0, 
                      right: 0, 
                      width: '28px', 
                      height: '28px', 
                      backgroundColor: '#ffffff',
                      border: '3px solid #ffffff',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      cursor: 'pointer'
                    }}>
                    <Camera size={14} color="#007B8F" />
                  </button>
                </div>
                <h6 className="mb-1" style={{ 
                  fontFamily: 'Inter, sans-serif', 
                  fontWeight: '600',
                  color: '#1f2937',
                  fontSize: '16px'
                }}>
                  {formData.firstName} {formData.lastName}
                </h6>
                <small style={{ 
                  color: '#6b7280',
                  fontSize: '13px',
                  fontWeight: '500'
                }}>
                  {formData.role}
                </small>
              </div>

              <nav className="nav flex-column">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    className={`nav-link text-start d-flex align-items-center gap-3 ${
                      activeTab === tab.id ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      border: 'none',
                      backgroundColor: activeTab === tab.id ? '#007B8F' : 'transparent',
                      color: activeTab === tab.id ? 'white' : '#6b7280',
                      borderRadius: '10px',
                      marginBottom: '6px',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      fontWeight: '500',
                      padding: '12px 16px',
                      transition: 'all 0.2s ease',
                      boxShadow: activeTab === tab.id ? '0 4px 12px rgba(0, 123, 143, 0.3)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeTab !== tab.id) {
                        e.target.style.backgroundColor = '#f3f4f6';
                        e.target.style.color = '#374151';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeTab !== tab.id) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#6b7280';
                      }
                    }}>
                    <tab.icon size={18} />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div 
            className="flex-grow-1 overflow-auto"
            style={{ backgroundColor: '#ffffff' }}>
            <div className="p-5">
              {/* Personal Info Tab */}
              {activeTab === 'personal' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-5">
                    <div>
                      <h5 className="mb-1" style={{ 
                        fontFamily: 'Inter, sans-serif', 
                        fontWeight: '600',
                        color: '#1f2937',
                        fontSize: '18px'
                      }}>
                        Personal Information
                      </h5>
                      <p className="mb-0" style={{ 
                        color: '#6b7280',
                        fontSize: '14px'
                      }}>
                        Update your personal details and contact information
                      </p>
                    </div>
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="btn d-flex align-items-center gap-2"
                      style={{
                        backgroundColor: isEditing ? '#dc2626' : '#007B8F',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        fontFamily: 'Inter, sans-serif',
                        boxShadow: '0 2px 8px rgba(0, 123, 143, 0.3)',
                        transition: 'all 0.2s ease'
                      }}>
                      <Edit size={16} />
                      {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                  </div>

                  {/* 3x2 Grid Layout - Using Flexbox */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Row 1 */}
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label className="form-label" style={{...labelStyle, fontSize: '13px', marginBottom: '6px'}}>
                          First Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          style={{...formInputStyle, padding: '10px 12px', fontSize: '13px'}}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="form-label" style={{...labelStyle, fontSize: '13px', marginBottom: '6px'}}>
                          Last Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          style={{...formInputStyle, padding: '10px 12px', fontSize: '13px'}}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="form-label" style={{...labelStyle, fontSize: '13px', marginBottom: '6px'}}>
                          Email
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: '#f9fafb',
                            border: '2px solid #e5e7eb',
                            borderRight: 'none',
                            borderRadius: '8px 0 0 8px',
                            padding: '10px 8px'
                          }}>
                            <Mail size={14} color="#6b7280" />
                          </span>
                          <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            style={{
                              ...formInputStyle,
                              borderLeft: 'none',
                              borderRadius: '0 8px 8px 0',
                              padding: '10px 12px',
                              fontSize: '13px'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Row 2 */}
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label className="form-label" style={{...labelStyle, fontSize: '13px', marginBottom: '6px'}}>
                          Phone
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: '#f9fafb',
                            border: '2px solid #e5e7eb',
                            borderRight: 'none',
                            borderRadius: '8px 0 0 8px',
                            padding: '10px 8px'
                          }}>
                            <Phone size={14} color="#6b7280" />
                          </span>
                          <input
                            type="tel"
                            className="form-control"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            style={{
                              ...formInputStyle,
                              borderLeft: 'none',
                              borderRadius: '0 8px 8px 0',
                              padding: '10px 12px',
                              fontSize: '13px'
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="form-label" style={{...labelStyle, fontSize: '13px', marginBottom: '6px'}}>
                          Department
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: '#f9fafb',
                            border: '2px solid #e5e7eb',
                            borderRight: 'none',
                            borderRadius: '8px 0 0 8px',
                            padding: '10px 8px'
                          }}>
                            <Briefcase size={14} color="#6b7280" />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            name="department"
                            value={formData.department}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            style={{
                              ...formInputStyle,
                              borderLeft: 'none',
                              borderRadius: '0 8px 8px 0',
                              padding: '10px 12px',
                              fontSize: '13px'
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="form-label" style={{...labelStyle, fontSize: '13px', marginBottom: '6px'}}>
                          Position
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: '#f9fafb',
                            border: '2px solid #e5e7eb',
                            borderRight: 'none',
                            borderRadius: '8px 0 0 8px',
                            padding: '10px 8px'
                          }}>
                            <Award size={14} color="#6b7280" />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            name="position"
                            value={formData.position}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            style={{
                              ...formInputStyle,
                              borderLeft: 'none',
                              borderRadius: '0 8px 8px 0',
                              padding: '10px 12px',
                              fontSize: '13px'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Bio - Full Width */}
                    <div style={{ marginTop: '8px' }}>
                      <label className="form-label" style={{...labelStyle, fontSize: '13px', marginBottom: '6px'}}>
                        Bio
                      </label>
                      <textarea
                        className="form-control"
                        name="bio"
                        rows="2"
                        value={formData.bio}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Tell us about yourself..."
                        style={{...formInputStyle, padding: '10px 12px', fontSize: '13px'}}
                      />
                    </div>
                  </div>

                  {isEditing && (
                    <div className="d-flex gap-3 mt-5">
                      <button
                        onClick={handleSave}
                        className="btn d-flex align-items-center gap-2"
                        style={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          fontFamily: 'Inter, sans-serif',
                          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                          transition: 'all 0.2s ease'
                        }}>
                        <Save size={16} />
                        Save Changes
                      </button>
                      <button
                        onClick={handleCancel}
                        className="btn d-flex align-items-center gap-2"
                        style={{
                          backgroundColor: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          fontFamily: 'Inter, sans-serif',
                          boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)',
                          transition: 'all 0.2s ease'
                        }}>
                        <X size={16} />
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Preferences Tab */}
              {activeTab === 'preferences' && (
                <div>
                  <h5 className="mb-3" style={{ 
                    fontFamily: 'Inter, sans-serif', 
                    fontWeight: '600',
                    color: '#1f2937',
                    fontSize: '18px'
                  }}>
                    Notification Preferences
                  </h5>

                  <div className="row g-3">
                    {/* Email Notifications */}
                    <div className="col-md-6">
                      <div className="card" style={{ 
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                      }}>
                        <div className="card-body p-3">
                          <h6 className="card-title d-flex align-items-center gap-2 mb-3" style={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: '600',
                            color: '#1f2937',
                            fontSize: '14px'
                          }}>
                            <Mail size={16} color="#007B8F" />
                            Email Notifications
                          </h6>
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={preferences.notifications.email}
                              onChange={(e) => handlePreferenceChange('notifications', 'email', e.target.checked)}
                              style={{ transform: 'scale(1.1)' }}
                            />
                            <label className="form-check-label" style={{ 
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '13px'
                            }}>
                              Enable email notifications
                            </label>
                          </div>
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={preferences.notifications.maintenance}
                              onChange={(e) => handlePreferenceChange('notifications', 'maintenance', e.target.checked)}
                              style={{ transform: 'scale(1.1)' }}
                            />
                            <label className="form-check-label" style={{ 
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '13px'
                            }}>
                              Maintenance alerts
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={preferences.notifications.contracts}
                              onChange={(e) => handlePreferenceChange('notifications', 'contracts', e.target.checked)}
                              style={{ transform: 'scale(1.1)' }}
                            />
                            <label className="form-check-label" style={{ 
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '13px'
                            }}>
                              Contract updates
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* System Settings */}
                    <div className="col-md-6">
                      <div className="card" style={{ 
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                      }}>
                        <div className="card-body p-3">
                          <h6 className="card-title d-flex align-items-center gap-2 mb-3" style={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: '600',
                            color: '#1f2937',
                            fontSize: '14px'
                          }}>
                            <Bell size={16} color="#007B8F" />
                            System Settings
                          </h6>
                          <div className="mb-3">
                            <label className="form-label" style={{...labelStyle, fontSize: '13px', marginBottom: '6px'}}>
                              Language
                            </label>
                            <select 
                              className="form-select"
                              value={preferences.language}
                              onChange={(e) => setPreferences(prev => ({ ...prev, language: e.target.value }))}
                              style={{...formInputStyle, padding: '8px 12px', fontSize: '13px'}}>
                              <option value="en">English</option>
                              <option value="es">Spanish</option>
                              <option value="fr">French</option>
                              <option value="de">German</option>
                            </select>
                          </div>
                          <div className="mb-0">
                            <label className="form-label" style={{...labelStyle, fontSize: '13px', marginBottom: '6px'}}>
                              Theme
                            </label>
                            <select 
                              className="form-select"
                              value={preferences.theme}
                              onChange={(e) => setPreferences(prev => ({ ...prev, theme: e.target.value }))}
                              style={{...formInputStyle, padding: '8px 12px', fontSize: '13px'}}>
                              <option value="light">Light</option>
                              <option value="dark">Dark</option>
                              <option value="auto">Auto</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div>
                  <h5 className="mb-3" style={{ 
                    fontFamily: 'Inter, sans-serif', 
                    fontWeight: '600',
                    color: '#1f2937',
                    fontSize: '18px'
                  }}>
                    Security Settings
                  </h5>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="card" style={{ 
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                      }}>
                        <div className="card-body p-3">
                          <h6 className="card-title d-flex align-items-center gap-2 mb-2" style={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: '600',
                            color: '#1f2937',
                            fontSize: '14px'
                          }}>
                            <Key size={16} color="#007B8F" />
                            Password
                          </h6>
                          <p className="text-muted small mb-3" style={{ 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px'
                          }}>
                            Last changed: {security.passwordLastChanged}
                          </p>
                          <button className="btn" style={{
                            backgroundColor: '#007B8F',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '13px',
                            fontWeight: '500',
                            fontFamily: 'Inter, sans-serif'
                          }}>
                            Change Password
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="card" style={{ 
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                      }}>
                        <div className="card-body p-3">
                          <h6 className="card-title d-flex align-items-center gap-2 mb-2" style={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: '600',
                            color: '#1f2937',
                            fontSize: '14px'
                          }}>
                            <Shield size={16} color="#007B8F" />
                            Two-Factor Authentication
                          </h6>
                          <div className="form-check form-switch mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={security.twoFactor}
                              onChange={(e) => setSecurity(prev => ({ ...prev, twoFactor: e.target.checked }))}
                              style={{ transform: 'scale(1.1)' }}
                            />
                            <label className="form-check-label" style={{ 
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '13px'
                            }}>
                              Enable 2FA
                            </label>
                          </div>
                          <p className="text-muted small" style={{ 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px'
                          }}>
                            Add an extra layer of security to your account
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Activity Tab */}
              {activeTab === 'activity' && (
                <div>
                  <h5 className="mb-3" style={{ 
                    fontFamily: 'Inter, sans-serif', 
                    fontWeight: '600',
                    color: '#1f2937',
                    fontSize: '18px'
                  }}>
                    Account Activity
                  </h5>

                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center gap-3 p-3 bg-light rounded" style={{
                        backgroundColor: '#f8fafc',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px'
                      }}>
                        <Calendar size={20} className="text-primary" />
                        <div>
                          <div className="fw-medium" style={{ 
                            fontFamily: 'Inter, sans-serif', 
                            color: '#1f2937',
                            fontSize: '14px'
                          }}>
                            Member Since
                          </div>
                          <small className="text-muted" style={{ 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px'
                          }}>
                            January 15, 2024
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center gap-3 p-3 bg-light rounded" style={{
                        backgroundColor: '#f8fafc',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px'
                      }}>
                        <Clock size={20} className="text-success" />
                        <div>
                          <div className="fw-medium" style={{ 
                            fontFamily: 'Inter, sans-serif', 
                            color: '#1f2937',
                            fontSize: '14px'
                          }}>
                            Last Login
                          </div>
                          <small className="text-muted" style={{ 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px'
                          }}>
                            2 hours ago
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h6 style={{ 
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '12px',
                      fontSize: '16px'
                    }}>
                      Recent Activity
                    </h6>
                    <div className="list-group">
                      <div className="list-group-item d-flex justify-content-between align-items-center" style={{
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        marginBottom: '6px',
                        padding: '12px'
                      }}>
                        <div>
                          <div className="fw-medium" style={{ 
                            fontFamily: 'Inter, sans-serif', 
                            color: '#1f2937',
                            fontSize: '14px'
                          }}>
                            Logged in
                          </div>
                          <small className="text-muted" style={{ 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px'
                          }}>
                            2 hours ago
                          </small>
                        </div>
                        <span className="badge" style={{ 
                          backgroundColor: '#10b981',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}>
                          Success
                        </span>
                      </div>
                      <div className="list-group-item d-flex justify-content-between align-items-center" style={{
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        marginBottom: '6px',
                        padding: '12px'
                      }}>
                        <div>
                          <div className="fw-medium" style={{ 
                            fontFamily: 'Inter, sans-serif', 
                            color: '#1f2937',
                            fontSize: '14px'
                          }}>
                            Updated profile
                          </div>
                          <small className="text-muted" style={{ 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px'
                          }}>
                            1 day ago
                          </small>
                        </div>
                        <span className="badge" style={{ 
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}>
                          Info
                        </span>
                      </div>
                      <div className="list-group-item d-flex justify-content-between align-items-center" style={{
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                        <div>
                          <div className="fw-medium" style={{ 
                            fontFamily: 'Inter, sans-serif', 
                            color: '#1f2937',
                            fontSize: '14px'
                          }}>
                            Changed password
                          </div>
                          <small className="text-muted" style={{ 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px'
                          }}>
                            1 week ago
                          </small>
                        </div>
                        <span className="badge" style={{ 
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}>
                          Security
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;